import { Controller, useForm } from 'react-hook-form';
import Modal from 'react-modal';

// material ui
import { Box, Button, Divider, Grid, Stack, TextField, Typography } from '@mui/material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

// graphql
import { useAdminQuery, useEditCashierMutation, usePlCashierProfileQuery, useUpdateAdminMutation } from '@/generated/graphql';

// interfaces
import type { ICurrentUserLogin, IEditProfileInput } from '@/shared/interfaces';

// enums
import { UserType } from '@/shared/enums';
import { useEffect } from 'react';

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currentUserLogin: ICurrentUserLogin | null;
  refetch: () => void;
}

export const ModalEditProfile = ({ open, setOpen, currentUserLogin, refetch }: IProps) => {
  const handleClose = () => setOpen(false);

  const { reset, handleSubmit, control } = useForm<IEditProfileInput>({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      username: '',
    },
  });

  // query user details
  const adminQuery = useAdminQuery({
    variables: { id: currentUserLogin ? currentUserLogin.id : '' },
    skip: currentUserLogin && currentUserLogin.role === UserType.ADMIN.toString() ? false : true,
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      if (currentUserLogin) {
        reset((prev) => ({ ...prev, firstName: data.admin?.firstName ?? '', lastName: data.admin?.lastName ?? '' }));
      } else {
        console.error('currentUserLogin is undefine');
      }
    },
  });

  const cashierQuery = usePlCashierProfileQuery({
    skip: currentUserLogin && currentUserLogin.role === UserType.CASHIER.toString() ? false : true,
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      if (currentUserLogin) {
        reset((prev) => ({ ...prev, firstName: data.cashierProfile?.firstName ?? '', lastName: data.cashierProfile?.lastName ?? '' }));
      } else {
        console.error('currentUserLogin is undefine');
      }
    },
  });

  const [editCashier] = useEditCashierMutation();
  const [updateAdmin] = useUpdateAdminMutation();

  const onSubmit = async (dataInput: IEditProfileInput) => {
    const { firstName, lastName, username } = dataInput;
    if (currentUserLogin && currentUserLogin.role === UserType.CASHIER.toString()) {
      await editCashier({
        variables: {
          input: {
            firstName,
            lastName,
            username,
            id: currentUserLogin.id,
          },
        },
      });
      cashierQuery.refetch();
    } else if (currentUserLogin && currentUserLogin.role === UserType.ADMIN.toString()) {
      await updateAdmin({
        variables: {
          input: {
            firstName,
            lastName,
            username,
            adminId: currentUserLogin.id,
          },
        },
      });
      adminQuery.refetch();
    }
    refetch();

    handleClose();
    reset();
  };

  useEffect(() => {
    if (currentUserLogin) {
      reset((prev) => ({ ...prev, username: currentUserLogin.username }));
    }
  }, [currentUserLogin]);

  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={handleClose}
        style={{
          overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            color: 'inherit',
            backgroundColor: 'rgba(25, 25, 25, 0.75)',
          },
          content: {
            width: '400px',
            overflow: 'unset',
            position: 'absolute',
            margin: '0.1% auto',
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            padding: 0,
            marginRight: '-50%',
            transform: 'translate(-50%, -48vh)',
            border: '0px',
            background: 'transparent',
            WebkitOverflowScrolling: 'touch',
            borderRadius: '4px',
            outline: 'none',
          },
        }}>
        <Box
          sx={{
            backgroundColor: '#fff',
            overflow: 'unset',
            borderRadius: '5px',
          }}
          mt={9}>
          <Box sx={{ px: 5, py: 2 }} alignItems={'center'}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography gutterBottom variant="subtitle2" m={0}>
                Edit Profile
              </Typography>
              <ManageAccountsIcon />
            </Stack>
          </Box>
          <Divider />

          <Box px={5} pt={2} pb={2}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid>
                <Controller
                  name="firstName"
                  control={control}
                  rules={{
                    required: 'First name is required',
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="First Name"
                      type="text"
                      variant="standard"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error ? fieldState.error.message : null}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ mt: 5 }}
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  rules={{
                    required: 'Last name is required',
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Last Name"
                      type="text"
                      variant="standard"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error ? fieldState.error.message : null}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ mt: 5 }}
                    />
                  )}
                />

                <Controller
                  name="username"
                  control={control}
                  rules={{
                    required: 'Username is required',
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Username"
                      type="text"
                      variant="standard"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error ? fieldState.error.message : null}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ mt: 5 }}
                    />
                  )}
                />
              </Grid>
              <Grid container justifyContent={'end'} mt={5}>
                <Button
                  type="submit"
                  size="small"
                  variant="contained"
                  sx={{
                    background: 'grey',
                    fontSize: 10,
                    fontWeight: 600,
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    textTransform: 'unset',
                  }}
                  onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  sx={{
                    ml: 1,
                    fontSize: 10,
                    fontWeight: 600,
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    textTransform: 'unset',
                  }}
                  type="submit"
                  size="small"
                  variant="contained">
                  Submit
                </Button>
              </Grid>
            </form>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
